/**
 * 屏幕 3：详情页（左立绘 + 右档案）
 * 与四宫格共用同一 combo，保证视觉完全一致
 */

import { generateMonster, animateIdle, type MonsterFrames } from "../spriteLib";
import { createSnow } from "../snow";
import { addToZoo, getBiome, BIOME_LABELS } from "../zoo";
import type { CharAnalysis, MonsterDesc } from "../../shared/types";
import type { Screen2Combo } from "./screen2";

export interface Screen3Deps {
  root: HTMLElement;
  analysis: CharAnalysis;
  monster: MonsterDesc;
  combo: Screen2Combo;
  onBack: () => void;
  onRegenerate: () => void;
  onGoZoo?: () => void;
}

export interface Screen3Controller {
  destroy: () => void;
}

export function createScreen3(deps: Screen3Deps): Screen3Controller {
  const { root, analysis, monster, combo, onBack, onRegenerate, onGoZoo } = deps;

  // 进入详情页 = 入园
  addToZoo(monster, combo, analysis.raw);

  const destroySnow = createSnow(root);

  const screen = document.createElement("div");
  screen.className = "screen-3";

  const portrait = document.createElement("div");
  portrait.className = "portrait";
  const canvas = document.createElement("canvas");
  const frames: MonsterFrames = generateMonster(
    monster,
    undefined,
    combo.bodyType,
    combo.paletteName,
    combo.variant
  );
  const stopAnim = animateIdle(canvas, frames, 4);
  portrait.appendChild(canvas);
  screen.appendChild(portrait);

  const archive = document.createElement("div");
  archive.className = "archive";

  const scpIdEl = document.createElement("div");
  scpIdEl.className = "scpid";
  scpIdEl.textContent = monster.scpId;
  archive.appendChild(scpIdEl);

  const mkField = (label: string, value: string, valueClass = "") => {
    const f = document.createElement("div");
    f.className = "field";
    const l = document.createElement("div");
    l.className = "label";
    l.textContent = label;
    const v = document.createElement("div");
    v.className = `value ${valueClass}`.trim();
    v.textContent = value;
    f.appendChild(l);
    f.appendChild(v);
    return f;
  };

  const threatClass = `threat-${monster.threat}` as const;
  const biome = getBiome(combo.bodyType);
  archive.appendChild(mkField("威胁等级", monster.threat, threatClass));
  archive.appendChild(mkField("项目分类", monster.class));
  archive.appendChild(mkField("异化原型", `${combo.bodyType} (v${combo.variant + 1})`));
  archive.appendChild(mkField("色板", combo.paletteName));
  archive.appendChild(mkField("习性 / 圈养房间", `已入园 → ${BIOME_LABELS[biome]}域`));
  archive.appendChild(
    mkField("原名拆解", `${analysis.raw} · ${analysis.themes.slice(0, 4).join(" / ")}`)
  );
  archive.appendChild(mkField("主要特征", monster.features.join("、")));

  const descField = document.createElement("div");
  descField.className = "field";
  const descLabel = document.createElement("div");
  descLabel.className = "label";
  descLabel.textContent = "档案描述";
  const descValue = document.createElement("div");
  descValue.className = "value description";
  descValue.textContent = monster.description || "（无描述）";
  descField.appendChild(descLabel);
  descField.appendChild(descValue);
  archive.appendChild(descField);

  screen.appendChild(archive);

  const actions = document.createElement("div");
  actions.className = "actions";
  actions.innerHTML = `
    <button class="secondary">返回画廊</button>
    <button class="nav-btn">查看图鉴</button>
    <button class="primary">再生</button>
  `;
  const backBtn = actions.querySelector(".secondary") as HTMLButtonElement;
  const regenBtn = actions.querySelector(".primary") as HTMLButtonElement;
  const zooBtn = actions.querySelector(".nav-btn") as HTMLButtonElement;
  backBtn.addEventListener("click", () => onBack());
  regenBtn.addEventListener("click", () => onRegenerate());
  if (onGoZoo) zooBtn.addEventListener("click", () => onGoZoo());
  else zooBtn.style.display = "none";
  screen.appendChild(actions);

  root.appendChild(screen);

  const destroy = () => {
    stopAnim();
    destroySnow();
    screen.remove();
  };

  return { destroy };
}
